
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const VirtualStagingPage = () => {
  const [selectedPackage, setSelectedPackage] = useState('');
  const [formData, setFormData] = useState({
    propertyType: '',
    rooms: '',
    urgency: 'standard',
    customerName: '',
    email: '',
    phone: '',
    propertyAddress: '',
    specialRequests: ''
  });

  const packages = [
    {
      id: 'basic-staging',
      name: 'Basic Virtual Staging',
      price: 89,
      rooms: '1-3 rooms',
      features: [
        'Professional furniture placement',
        'Modern design aesthetic',
        '3-5 business day delivery',
        'High-resolution images',
        '2 revision rounds'
      ]
    },
    {
      id: 'premium-staging',
      name: 'Premium Virtual Staging',
      price: 149,
      rooms: '4-6 rooms',
      features: [
        'Custom furniture selection',
        'Multiple design styles',
        '2-3 business day delivery',
        'Ultra high-resolution images',
        'Unlimited revisions',
        'Before/after comparisons'
      ],
      popular: true
    },
    {
      id: 'luxury-staging',
      name: 'Luxury Virtual Staging',
      price: 249,
      rooms: '7+ rooms',
      features: [
        'Premium furniture brands',
        'Luxury design consultation',
        '24-48 hour delivery',
        'Ultra high-resolution images',
        'Unlimited revisions',
        'Multiple design options',
        'Dedicated project manager'
      ]
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedPkg = packages.find(pkg => pkg.id === selectedPackage);
    if (!selectedPkg) return;

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-payment-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packageType: selectedPkg.name,
          price: selectedPkg.price,
          customerName: formData.customerName,
          email: formData.email,
          phone: formData.phone,
          propertyType: formData.propertyType,
          location: formData.propertyAddress,
          urgency: formData.urgency,
          specialRequests: formData.specialRequests,
          serviceType: 'virtual_staging'
        })
      });

      const { url } = await response.json();
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error creating payment session:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <div className="pt-20 pb-16 bg-gradient-to-br from-purple-50 to-white relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/d480247c915b54d3357881bc20dbf0de.jpg')`
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Virtual Staging for
              <span className="text-purple-600"> Airbnb Properties</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Transform empty spaces into inviting, bookable properties with professional virtual furniture and décor. Increase bookings by up to 83% with staged photos.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center">
                <i className="ri-check-line text-green-600 mr-2"></i>
                <span>24-48 Hour Delivery</span>
              </div>
              <div className="flex items-center">
                <i className="ri-check-line text-green-600 mr-2"></i>
                <span>Multiple Design Styles</span>
              </div>
              <div className="flex items-center">
                <i className="ri-check-line text-green-600 mr-2"></i>
                <span>Unlimited Revisions</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Before/After Gallery */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Before & After Transformations</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Empty%20living%20room%20before%20virtual%20staging%20bare%20walls%20no%20furniture%20hardwood%20floors%20large%20windows%20unfurnished%20space%20real%20estate%20photography&width=400&height=300&seq=before-1&orientation=landscape"
                  alt="Before staging"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  Before
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautifully%20staged%20living%20room%20with%20modern%20furniture%20sofa%20coffee%20table%20decorative%20pillows%20artwork%20plants%20warm%20lighting%20inviting%20cozy%20interior%20design&width=400&height=300&seq=after-1&orientation=landscape"
                  alt="After staging"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  After
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Empty%20bedroom%20unfurnished%20bare%20walls%20carpet%20flooring%20single%20window%20vacant%20property%20real%20estate%20listing%20photography&width=400&height=300&seq=before-2&orientation=landscape"
                  alt="Before staging"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  Before
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Stylish%20staged%20bedroom%20with%20queen%20bed%20nightstands%20lamps%20throw%20pillows%20artwork%20curtains%20plants%20modern%20furniture%20welcoming%20atmosphere&width=400&height=300&seq=after-2&orientation=landscape"
                  alt="After staging"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  After
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Empty%20kitchen%20dining%20area%20bare%20counters%20no%20furniture%20vacant%20unfurnished%20space%20tile%20flooring%20basic%20appliances&width=400&height=300&seq=before-3&orientation=landscape"
                  alt="Before staging"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  Before
                </div>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Beautifully%20staged%20kitchen%20dining%20room%20with%20dining%20table%20chairs%20bar%20stools%20decorative%20bowls%20plants%20artwork%20modern%20accessories%20welcoming%20space&width=400&height=300&seq=after-3&orientation=landscape"
                  alt="After staging"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  After
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Packages Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Virtual Staging Packages</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg) => (
              <div 
                key={pkg.id}
                className={`relative bg-white rounded-lg shadow-lg border-2 transition-all cursor-pointer ${
                  selectedPackage === pkg.id ? 'border-purple-500 shadow-xl' : 'border-gray-200 hover:border-purple-300'
                } ${pkg.popular ? 'ring-2 ring-purple-200' : ''}`}
                onClick={() => setSelectedPackage(pkg.id)}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-purple-600 mb-2">
                    ${pkg.price}
                  </div>
                  <p className="text-gray-600 mb-6">{pkg.rooms}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <i className="ri-check-line text-green-600 mr-3 mt-0.5"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex items-center justify-center">
                    <div className={`w-6 h-6 rounded-full border-2 ${
                      selectedPackage === pkg.id ? 'bg-purple-600 border-purple-600' : 'border-gray-300'
                    } flex items-center justify-center`}>
                      {selectedPackage === pkg.id && (
                        <i className="ri-check-line text-white text-sm"></i>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Booking Form */}
      {selectedPackage && (
        <div className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Book Your Virtual Staging</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Property Type *
                    </label>
                    <select
                      name="propertyType"
                      required
                      value={formData.propertyType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Select property type</option>
                      <option value="apartment">Apartment</option>
                      <option value="house">House</option>
                      <option value="condo">Condo</option>
                      <option value="villa">Villa</option>
                      <option value="studio">Studio</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Rooms *
                    </label>
                    <select
                      name="rooms"
                      required
                      value={formData.rooms}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Select rooms</option>
                      <option value="1-2">1-2 rooms</option>
                      <option value="3-4">3-4 rooms</option>
                      <option value="5-6">5-6 rooms</option>
                      <option value="7+">7+ rooms</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="customerName"
                      required
                      value={formData.customerName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Delivery Timeline
                    </label>
                    <select
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="standard">Standard (3-5 days)</option>
                      <option value="rush">Rush (24-48 hours) +$50</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property Address *
                  </label>
                  <input
                    type="text"
                    name="propertyAddress"
                    required
                    value={formData.propertyAddress}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="Enter complete property address"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Special Requests or Design Preferences
                  </label>
                  <textarea
                    name="specialRequests"
                    rows={4}
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="Any specific furniture styles, colors, or design preferences..."
                    maxLength={500}
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Max 500 characters</p>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Order Summary</h3>
                  <div className="flex justify-between items-center">
                    <span>{packages.find(p => p.id === selectedPackage)?.name}</span>
                    <span className="font-semibold">${packages.find(p => p.id === selectedPackage)?.price}</span>
                  </div>
                  {formData.urgency === 'rush' && (
                    <div className="flex justify-between items-center mt-2">
                      <span>Rush delivery fee</span>
                      <span className="font-semibold">+$50</span>
                    </div>
                  )}
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-purple-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors whitespace-nowrap"
                >
                  Proceed to Payment
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Frequently Asked Questions</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What photos do I need to provide for virtual staging?</h3>
              <p className="text-gray-600">You'll need high-resolution photos of the empty rooms you want staged. We recommend professional photography for best results, but high-quality smartphone photos can work too.</p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How realistic do virtually staged photos look?</h3>
              <p className="text-gray-600">Our virtual staging uses advanced 3D modeling and professional-grade software to create photorealistic results. Most viewers cannot distinguish between virtually staged and physically staged photos.</p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Can I request specific furniture styles or brands?</h3>
              <p className="text-gray-600">Yes! We offer various design styles including modern, traditional, scandinavian, and luxury. Premium packages include access to brand-name furniture for even more realistic staging.</p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Do I need to disclose that photos are virtually staged?</h3>
              <p className="text-gray-600">Yes, most platforms including Airbnb require disclosure that photos are virtually staged. We provide suggested disclosure language to keep you compliant.</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VirtualStagingPage;
